import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Training Solutions';
const DEFAULT_DESCRIPTION = 'India\'s leading corporate training marketplace. Connect with 500+ expert trainers across Leadership, Technology, Sales, Compliance & more. Find trainers or post training requirements globally.';
const DEFAULT_IMAGE = '/og-image.png';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  type = 'website',
  image = DEFAULT_IMAGE,
  noIndex = false,
  jsonLd = null
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Match Corporate Trainers with Companies`;
  const url = path ? `https://trainingsolutions.in${path}` : 'https://trainingsolutions.in';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="googlebot" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large'} />

      {/* Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-built JSON-LD schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Training Solutions",
  "description": "India's leading corporate training marketplace connecting expert trainers with companies globally.",
  "url": "https://trainingsolutions.in",
  "logo": "https://trainingsolutions.in/logo.png",
  "sameAs": [],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["English", "Hindi"]
  },
  "areaServed": {
    "@type": "GeoShape",
    "name": "Global"
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Training Solutions",
  "url": "https://trainingsolutions.in",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://trainingsolutions.in/search?q={search_term_string}&type=trainers"
    },
    "query-input": "required name=search_term_string"
  }
};

export const trainerProfileSchema = (trainer) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": trainer.name,
  "jobTitle": "Corporate Trainer",
  "description": trainer.bio || `Expert trainer specializing in ${(trainer.skills || []).join(', ')}`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": trainer.location,
    "addressCountry": trainer.country
  },
  "knowsAbout": trainer.skills || [],
  "knowsLanguage": trainer.languages || [],
  "aggregateRating": trainer.rating ? {
    "@type": "AggregateRating",
    "ratingValue": trainer.rating,
    "reviewCount": trainer.total_reviews || 0,
    "bestRating": 5
  } : undefined
});

export const jobPostingSchema = (job) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": job.title,
  "description": job.description,
  "datePosted": job.created_at,
  "validThrough": job.end_date,
  "employmentType": "CONTRACT",
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": job.location
    }
  },
  "baseSalary": {
    "@type": "MonetaryAmount",
    "currency": "INR",
    "value": {
      "@type": "QuantitativeValue",
      "value": job.budget
    }
  },
  "skills": job.skills_required?.join(', '),
  "hiringOrganization": {
    "@type": "Organization",
    "name": "Training Solutions"
  }
});

export default SEO;
